import React, { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { failed } from '../lib/toast'

// Combined-audit group panel (Phase 1). Office-only in practice (a DB trigger + RLS block auditors).
// Lets the office turn a job into the PRIMARY of a combined audit and link up to 3 more jobs (same scheme,
// same assigned auditors, each with planning manpower + a client UEN). Max 4 total is DB-enforced too.
export default function CombinedAuditPanel({ job, onChange }) {
  const { profile } = useAuth()
  const [group, setGroup] = useState(null)
  const [members, setMembers] = useState([])
  const [candidates, setCandidates] = useState([])
  const [pick, setPick] = useState('')
  const [busy, setBusy] = useState(false)

  const groupId = job?.combined_group_id || null

  const load = useCallback(async () => {
    if (!groupId) { setGroup(null); setMembers([]); return }
    const { data: g } = await supabase.from('combined_audit_groups').select('*').eq('id', groupId).maybeSingle()
    setGroup(g || null)
    const { data: m } = await supabase.from('jobs').select('id, is_combined_primary, planning_num_employees, clients(name, uen)').eq('combined_group_id', groupId)
    setMembers((m || []).sort((a, b) => (b.is_combined_primary ? 1 : 0) - (a.is_combined_primary ? 1 : 0)))
  }, [groupId])
  useEffect(() => { load() }, [load])

  async function loadCandidates() {
    const { data } = await supabase.from('jobs')
      .select('id, audit_type, auditor_id, auditor2_id, planning_num_employees, clients(name, uen)')
      .is('combined_group_id', null).eq('audit_type', job.audit_type).neq('id', job.id).neq('status', 'Cancelled')
    // Same team + planning manpower + a client UEN (the fan-out signs each member's snapshot with the shared team).
    const ok = (data || []).filter(j =>
      (j.auditor_id || null) === (job.auditor_id || null) &&
      (j.auditor2_id || null) === (job.auditor2_id || null) &&
      j.planning_num_employees != null && j.clients?.uen)
    setCandidates(ok)
  }

  async function createGroup() {
    setBusy(true)
    const { data: g, error } = await supabase.from('combined_audit_groups').insert({
      scheme: job.audit_type, audit_date: job.audit_date, primary_job_id: job.id, created_by: profile?.id || null,
    }).select().maybeSingle()
    if (error || !g) { setBusy(false); return failed("Couldn't create the combined audit", error?.message) }
    const { error: e2 } = await supabase.from('jobs').update({ combined_group_id: g.id, is_combined_primary: true }).eq('id', job.id)
    setBusy(false)
    if (e2) return failed("Couldn't set the main company", e2.message)
    onChange && onChange()
  }

  async function linkJob() {
    if (!pick) return
    setBusy(true)
    // Operational status alignment ONLY: a member of an already-scheduled/confirmed combined audit should not
    // sit at the "Enquiry" default just because it was created manually. Mirror the primary's Scheduled/Confirmed
    // status onto the member — nothing else (no audit_date, auditor, application_type or entity fields touched).
    const patch = { combined_group_id: groupId, is_combined_primary: false }
    if (job.status === 'Scheduled' || job.status === 'Confirmed') patch.status = job.status
    const { error } = await supabase.from('jobs').update(patch).eq('id', pick)
    setBusy(false)
    if (error) return failed("Couldn't link the company", error.message)   // e.g. max-4 or auditor guard from the DB trigger
    setPick(''); load(); loadCandidates()
  }

  async function unlink(mid) {
    if (!window.confirm('Remove this company from the combined audit?')) return
    setBusy(true)
    const { error } = await supabase.from('jobs').update({ combined_group_id: null, is_combined_primary: false }).eq('id', mid)
    setBusy(false)
    if (error) return failed("Couldn't unlink the company", error.message)
    load()
  }

  async function saveAttest(patch) {
    await supabase.from('combined_audit_groups').update({ ...patch, attested_by_name: profile?.name || null, attested_at: new Date().toISOString() }).eq('id', groupId)
    load()
  }

  const box = { border: '1px solid var(--border, #e2e2e2)', borderRadius: 8, padding: 14, marginTop: 14, background: 'var(--card, #fff)' }

  // Not yet a combined audit → offer to start one (only meaningful once a client + audit type exist).
  if (!groupId) {
    return (
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Combined audit</div>
        <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 10 }}>
          If several companies share the same management, location, business nature and RM/RA team, you can audit them
          together and issue one combined report while each keeps its own bizSAFE certificate.
        </div>
        <button className="sm primary" disabled={busy || !job?.audit_type} onClick={createGroup}>Make this the primary of a combined audit</button>
      </div>
    )
  }

  const totalManpower = members.reduce((s, m) => s + (parseInt(m.planning_num_employees) || 0), 0)
  const atMax = members.length >= 4

  return (
    <div style={box}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>Combined audit group {job.is_combined_primary ? '(this is the primary)' : '(linked company)'}</div>

      {/* Members */}
      <div style={{ marginBottom: 10 }}>
        {members.map(m => (
          <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 14 }}>
            <span style={{ minWidth: 60, color: 'var(--text2)' }}>{m.is_combined_primary ? 'Primary' : 'Linked'}</span>
            <span style={{ flex: 1 }}>{m.clients?.name || '—'} <span style={{ color: 'var(--text2)' }}>· UEN {m.clients?.uen || '—'} · {m.planning_num_employees ?? '—'} pax</span></span>
            {!m.is_combined_primary && <button className="sm" onClick={() => unlink(m.id)} disabled={busy}>Unlink</button>}
          </div>
        ))}
        <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 4 }}>
          Combined manpower: <strong>{totalManpower}</strong> — suggested basis for CT17 man-days on the primary (office may override with justification).
        </div>
      </div>

      {/* Link another company (primary only) */}
      {job.is_combined_primary && !atMax && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 12 }}>
          <select value={pick} onFocus={loadCandidates} onChange={e => setPick(e.target.value)} style={{ flex: 1 }}>
            <option value="">Select a job to link (same scheme, same auditors, has manpower + UEN)…</option>
            {candidates.map(c => <option key={c.id} value={c.id}>{c.clients?.name} · UEN {c.clients?.uen}</option>)}
          </select>
          <button className="sm primary" onClick={linkJob} disabled={busy || !pick}>Link</button>
        </div>
      )}
      {atMax && <div style={{ fontSize: 13, color: 'var(--warn)', marginBottom: 12 }}>Maximum of 4 companies reached.</div>}

      {/* Eligibility attestation (primary only) */}
      {job.is_combined_primary && group && (
        <div style={{ borderTop: '1px solid var(--border,#eee)', paddingTop: 10 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Combined-audit eligibility (attest all)</div>
          {[['elig_same_management', 'Same management'], ['elig_same_location', 'Same audit location'],
            ['elig_same_business', 'Same / similar business nature'], ['elig_same_rmteam', 'Same RM / RA team']].map(([k, label]) => (
            <label key={k} style={{ display: 'block', fontSize: 13, marginBottom: 3 }}>
              <input type="checkbox" checked={!!group[k]} onChange={e => saveAttest({ [k]: e.target.checked })} /> {label}
            </label>
          ))}
          <input placeholder="Note (optional)" defaultValue={group.elig_note || ''} onBlur={e => saveAttest({ elig_note: e.target.value })}
            style={{ width: '100%', marginTop: 6 }} />
          {group.attested_by_name && <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 4 }}>Last attested by {group.attested_by_name}</div>}
        </div>
      )}
    </div>
  )
}
